/**
 * NotificationToast Component
 *
 * Shows real-time notifications for new leads from various sources
 * (Facebook, Instagram, WhatsApp, Web, etc.)
 *
 * True glassmorphism design matching QuoteViewingNotification.
 * Includes all features from QuoteViewingNotification:
 * - CalendarModal integration
 * - localStorage persistence for dismissed items
 * - Breathing animation for high priority
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications, useBreathingGreen, type Notification, type NotificationType } from '@/shared/hooks';
import { useTenant } from '@/shared/contexts/TenantContext';
import { XMarkIcon, ArrowRightIcon, GlobeAltIcon, UserPlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { PhoneIcon, ChatBubbleLeftIcon, EnvelopeIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { Badge } from '@/shared/components/ui/Badge';
import { PhoneModal } from '@/shared/components/PhoneModal';
import { CalendarModal } from '@/shared/components/CalendarModal';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// localStorage key for dismissed notifications
const DISMISSED_STORAGE_KEY = 'owly-dismissed-lead-notifications';

// Brand Icons (SVG components)
const FacebookIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TikTokIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

// Source configuration with React components
interface SourceConfig {
  icon: React.ReactNode;
  color: string;
  bgClass: string;
}

const getSourceConfig = (source: string): SourceConfig => {
  const configs: Record<string, SourceConfig> = {
    facebook: {
      icon: <FacebookIcon className="h-5 w-5 text-[#1877F2]" />,
      color: 'text-[#1877F2]',
      bgClass: 'bg-blue-500/25 border-blue-400/60',
    },
    facebook_leads: {
      icon: <FacebookIcon className="h-5 w-5 text-[#1877F2]" />,
      color: 'text-[#1877F2]',
      bgClass: 'bg-blue-500/25 border-blue-400/60',
    },
    instagram: {
      icon: <InstagramIcon className="h-5 w-5 text-[#E4405F]" />,
      color: 'text-[#E4405F]',
      bgClass: 'bg-gradient-to-br from-purple-500/25 to-pink-500/25 border-pink-400/60',
    },
    instagram_leads: {
      icon: <InstagramIcon className="h-5 w-5 text-[#E4405F]" />,
      color: 'text-[#E4405F]',
      bgClass: 'bg-gradient-to-br from-purple-500/25 to-pink-500/25 border-pink-400/60',
    },
    whatsapp: {
      icon: <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />,
      color: 'text-[#25D366]',
      bgClass: 'bg-green-500/25 border-green-400/60',
    },
    whatsapp_cloud: {
      icon: <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />,
      color: 'text-[#25D366]',
      bgClass: 'bg-green-500/25 border-green-400/60',
    },
    web: {
      icon: <GlobeAltIcon className="h-5 w-5 text-indigo-500" />,
      color: 'text-indigo-500',
      bgClass: 'bg-indigo-500/25 border-indigo-400/60',
    },
    landing: {
      icon: <GlobeAltIcon className="h-5 w-5 text-indigo-500" />,
      color: 'text-indigo-500',
      bgClass: 'bg-indigo-500/25 border-indigo-400/60',
    },
    tiktok: {
      icon: <TikTokIcon className="h-5 w-5 text-gray-800 dark:text-white" />,
      color: 'text-gray-800 dark:text-white',
      bgClass: 'bg-gray-800/25 dark:bg-white/25 border-gray-600/60 dark:border-white/60',
    },
    // Message received notifications
    message_received_whatsapp: {
      icon: <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />,
      color: 'text-[#25D366]',
      bgClass: 'bg-green-500/25 border-green-400/60',
    },
    message_received_facebook: {
      icon: <FacebookIcon className="h-5 w-5 text-[#1877F2]" />,
      color: 'text-[#1877F2]',
      bgClass: 'bg-blue-500/25 border-blue-400/60',
    },
    message_received_instagram: {
      icon: <InstagramIcon className="h-5 w-5 text-[#E4405F]" />,
      color: 'text-[#E4405F]',
      bgClass: 'bg-gradient-to-br from-purple-500/25 to-pink-500/25 border-pink-400/60',
    },
    message_received_email: {
      icon: <EnvelopeIcon className="h-5 w-5 text-indigo-500" />,
      color: 'text-indigo-500',
      bgClass: 'bg-indigo-500/25 border-indigo-400/60',
    },
    message_received_generic: {
      icon: <ChatBubbleLeftIcon className="h-5 w-5 text-gray-500" />,
      color: 'text-gray-500',
      bgClass: 'bg-gray-500/25 border-gray-400/60',
    },
    // Internal messaging notifications
    message_internal_dm: {
      icon: <ChatBubbleLeftIcon className="h-5 w-5 text-purple-500" />,
      color: 'text-purple-500',
      bgClass: 'bg-purple-500/25 border-purple-400/60',
    },
    message_internal_group: {
      icon: <UserPlusIcon className="h-5 w-5 text-indigo-500" />,
      color: 'text-indigo-500',
      bgClass: 'bg-indigo-500/25 border-indigo-400/60',
    },
    // Quote notifications
    quote: {
      icon: <DocumentTextIcon className="h-5 w-5 text-purple-500" />,
      color: 'text-purple-500',
      bgClass: 'bg-purple-500/25 border-purple-400/60',
    },
    quote_viewing: {
      icon: <DocumentTextIcon className="h-5 w-5 text-green-500" />,
      color: 'text-green-500',
      bgClass: 'bg-green-500/25 border-green-400/60',
    },
    quote_contact_requested: {
      icon: <DocumentTextIcon className="h-5 w-5 text-amber-500" />,
      color: 'text-amber-500',
      bgClass: 'bg-amber-500/25 border-amber-400/60',
    },
    quote_accepted: {
      icon: <DocumentTextIcon className="h-5 w-5 text-lime-500" />,
      color: 'text-lime-500',
      bgClass: 'bg-lime-500/25 border-lime-400/60',
    },
    quote_deferred: {
      icon: <DocumentTextIcon className="h-5 w-5 text-violet-500" />,
      color: 'text-violet-500',
      bgClass: 'bg-violet-500/25 border-violet-400/60',
    },
    quote_deferred_followup: {
      icon: <DocumentTextIcon className="h-5 w-5 text-violet-500" />,
      color: 'text-violet-500',
      bgClass: 'bg-violet-500/25 border-violet-400/60',
    },
    quote_expired: {
      icon: <DocumentTextIcon className="h-5 w-5 text-orange-500" />,
      color: 'text-orange-500',
      bgClass: 'bg-orange-500/25 border-orange-400/60',
    },
    default: {
      icon: <UserPlusIcon className="h-5 w-5 text-gray-500" />,
      color: 'text-gray-500',
      bgClass: 'bg-gray-500/25 border-gray-400/60',
    },
  };

  return configs[source] || configs.default;
};

// Format time ago
const formatTimeAgo = (date: string | null): string => {
  if (!date) return 'ahora';
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: es });
  } catch {
    return 'hace un momento';
  }
};

// Get badge variant based on notification type
const getBadgeVariant = (type: NotificationType): 'default' | 'amber' | 'lime' | 'blue' | 'pink' | 'green' => {
  // Quote notifications
  if (type === 'quote_accepted') {
    return 'lime';
  }
  if (type === 'quote_contact_requested') {
    return 'amber';
  }
  if (type === 'quote_deferred' || type === 'quote_deferred_followup') {
    return 'default';
  }
  if (type === 'quote_viewing') {
    return 'green';
  }
  if (type === 'quote_expired') {
    return 'amber';
  }
  if (type === 'quote_expiring_soon') {
    return 'amber';
  }
  // Message & Lead notifications
  if (type.includes('facebook')) {
    return 'blue';
  }
  if (type.includes('instagram')) {
    return 'pink';
  }
  if (type.includes('whatsapp')) {
    return 'green';
  }
  if (type.includes('tiktok')) {
    return 'default';
  }
  if (type.includes('email')) {
    return 'default';
  }
  return 'amber';
};

// ==================== NOTIFICATION CATEGORY SYSTEM ====================
//
// Instead of if/else chains, each notification type maps to a category.
// The category controls: icon, badge, action buttons, content rows, navigation.
// Adding a new notification type = adding one line to getNotificationCategory.

type NotificationCategory = 'lead' | 'message' | 'internal' | 'quote' | 'system';

/**
 * Classify notification by prefix → category.
 * This single function controls how every notification renders.
 */
const getNotificationCategory = (type: string): NotificationCategory => {
  if (type.startsWith('quote_')) return 'quote';
  if (type.startsWith('message_internal_')) return 'internal';
  if (type.startsWith('message_received_')) return 'message';
  if (type.startsWith('lead_')) return 'lead';
  if (type.startsWith('system_') || type.startsWith('task_')) return 'system';
  return 'lead'; // fallback for unknown types
};

// Check if notification is high priority (should breathe)
const isHighPriority = (notification: Notification): boolean => {
  if (notification.type === 'quote_contact_requested' || notification.type === 'quote_accepted' || notification.type === 'quote_deferred' || notification.type === 'quote_deferred_followup' || notification.type === 'quote_expiring_soon') {
    return true;
  }
  return notification.priority === 'high' || notification.priority === 'urgent';
};

/** Does this category show lead contact buttons (phone/whatsapp/email/calendar)? */
const showsContactActions = (category: NotificationCategory): boolean => {
  return category === 'lead';
};

/** Does this category show a message preview? */
const showsMessagePreview = (category: NotificationCategory): boolean => {
  return category === 'message' || category === 'internal';
};

// Get badge label based on notification type
const getBadgeLabel = (notification: Notification): string => {
  const category = getNotificationCategory(notification.type);

  // Quote badges
  const quoteBadges: Record<string, string> = {
    quote_accepted: 'Aceptada',
    quote_deferred: 'En espera',
    quote_deferred_followup: 'Recontactar',
    quote_contact_requested: 'Contacto',
    quote_viewing: 'Viendo',
    quote_expired: 'Expirada',
    quote_expiring_soon: 'Cotización por expirar',
  };
  if (quoteBadges[notification.type]) return quoteBadges[notification.type];

  // Lead special badges
  if (notification.type === 'lead_followup_due') return 'Seguimiento';

  // Message notifications (external + internal) — show count
  if (category === 'message' || category === 'internal') {
    const count = notification.metadata?.unread_message_count;
    if (category === 'internal') {
      const convType = notification.metadata?.conversation_type;
      const label = convType === 'direct' ? 'Mensaje directo' : 'Equipo';
      if (typeof count === 'number' && count > 1) return `${count} — ${label}`;
      return label;
    }
    if (typeof count === 'number' && count > 1) return `${count} mensajes`;
    return 'Mensaje';
  }

  return notification.metadata?.source_display as string || 'Lead';
};

// Source Details Component - Shows enriched source information
interface SourceDetailsProps {
  metadata: Notification['metadata'];
  type: NotificationType;
}

const SourceDetails: React.FC<SourceDetailsProps> = ({ metadata, type }) => {
  if (!metadata) return null;

  const source = metadata.source as string | undefined;
  const details: { icon: React.ReactNode; lines: string[] } = { icon: null, lines: [] };

  // Facebook/Instagram Lead Ads
  if (type.includes('facebook') || type.includes('instagram')) {
    const Icon = type.includes('facebook') ? FacebookIcon : InstagramIcon;
    details.icon = <Icon className="h-3.5 w-3.5 flex-shrink-0" />;

    if (metadata.campaign_name) {
      details.lines.push(`Campaña: ${metadata.campaign_name}`);
    }
    if (metadata.form_name) {
      details.lines.push(`Formulario: ${metadata.form_name}`);
    } else if (metadata.form_id) {
      details.lines.push(`Form ID: ${metadata.form_id}`);
    }
  }

  // WhatsApp
  else if (type.includes('whatsapp')) {
    details.icon = <WhatsAppIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#25D366]" />;

    if (metadata.channel_name) {
      details.lines.push(`Canal: ${metadata.channel_name}`);
    }
    if (metadata.contact_name) {
      details.lines.push(`Contacto: ${metadata.contact_name}`);
    }
  }

  // Web/Landing Page
  else if (type.includes('web') || source === 'website' || source === 'landing') {
    details.icon = <GlobeAltIcon className="h-3.5 w-3.5 flex-shrink-0 text-indigo-500" />;

    if (metadata.utm_source) {
      details.lines.push(`Fuente: ${metadata.utm_source}`);
    }
    if (metadata.utm_campaign) {
      details.lines.push(`Campaña: ${metadata.utm_campaign}`);
    }
    if (metadata.page_title) {
      details.lines.push(`Página: ${metadata.page_title}`);
    }
    // Si no hay detalles específicos, mostrar "Sitio Web" como fuente
    if (details.lines.length === 0) {
      details.lines.push('Sitio Web');
    }
  }

  // TikTok
  else if (type.includes('tiktok')) {
    details.icon = <TikTokIcon className="h-3.5 w-3.5 flex-shrink-0" />;

    if (metadata.campaign_name) {
      details.lines.push(`Campaña: ${metadata.campaign_name}`);
    }
  }

  // Quote (OrbitView)
  else if (source === 'quote') {
    details.icon = <span className="text-sm">📄</span>;

    if (metadata.unit_name || metadata.unit_code) {
      details.lines.push(`Unidad: ${metadata.unit_name || metadata.unit_code}`);
    }
    if (metadata.source_detail && typeof metadata.source_detail === 'string') {
      details.lines.push(metadata.source_detail);
    }
  }

  // Message received notifications
  else if (type.startsWith('message_received_')) {
    if (type.includes('whatsapp')) {
      details.icon = <WhatsAppIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#25D366]" />;
    } else if (type.includes('facebook')) {
      details.icon = <FacebookIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#1877F2]" />;
    } else if (type.includes('instagram')) {
      details.icon = <InstagramIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#E4405F]" />;
    } else {
      details.icon = <ChatBubbleLeftIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />;
    }

    if (metadata.channel_name) {
      details.lines.push(`Canal: ${metadata.channel_name}`);
    }
  }

  // Internal messaging notifications
  else if (type.startsWith('message_internal_')) {
    details.icon = <ChatBubbleLeftIcon className="h-3.5 w-3.5 flex-shrink-0 text-purple-500" />;
    if (metadata.group_name) {
      details.lines.push(`Grupo: ${metadata.group_name}`);
    }
    if (metadata.sender_name && !metadata.group_name) {
      details.lines.push(`De: ${metadata.sender_name}`);
    }
  }

  // Manual / Generic
  else if (source === 'manual' || source === 'generic' || !source) {
    details.icon = <UserPlusIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />;
    details.lines.push('Página de Leads');
  }

  // No details to show
  if (details.lines.length === 0) return null;

  return (
    <div className="mt-1.5 pt-1.5 border-t border-gray-200/50 dark:border-gray-700/50">
      <div className="flex items-start gap-1.5">
        <div className="mt-0.5">{details.icon}</div>
        <div className="flex-1 min-w-0 space-y-0.5">
          {details.lines.map((line, idx) => (
            <p key={idx} className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {line}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Load dismissed notification IDs from localStorage */
const loadDismissedNotifications = (): Set<string> => {
  try {
    const stored = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { ids: string[]; timestamp: number };
      // Expire after 24 hours to avoid infinite accumulation
      if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return new Set(parsed.ids);
      }
    }
  } catch { /* ignore */ }
  return new Set();
};

/** Save dismissed notification IDs to localStorage */
const saveDismissedNotifications = (ids: Set<string>) => {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify({
      ids: Array.from(ids),
      timestamp: Date.now(),
    }));
  } catch { /* ignore */ }
};

interface NotificationItemProps {
  notification: Notification;
  onDismiss: () => void;
  onPhoneClick: (notification: Notification) => void;
  onCalendarClick: (notification: Notification) => void;
  phoneActioned?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss,
  onPhoneClick,
  onCalendarClick,
  phoneActioned,
}) => {
  const { setSelectedProject, projects } = useTenant();
  const navigate = useNavigate();

  // Breathing animation for high priority notifications (disabled after phone action)
  const shouldBreathe = !phoneActioned && isHighPriority(notification);
  const breatheStyle = useBreathingGreen(shouldBreathe);

  // Update time every 10 seconds
  const [timeAgo, setTimeAgo] = React.useState(formatTimeAgo(notification.created_at));

  React.useEffect(() => {
    setTimeAgo(formatTimeAgo(notification.created_at));
    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(notification.created_at));
    }, 10000);
    return () => clearInterval(interval);
  }, [notification.created_at]);

  // Navigate to lead detail page directly
  const handleViewLead = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Switch to correct project if needed
    const projectId = notification.metadata?.project_id;
    if (projectId && projects.length > 0) {
      const targetProject = projects.find(p => p.id === projectId);
      if (targetProject) {
        setSelectedProject(targetProject);
        // Wait for project context to update
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    // Navigate to leads page with search param to filter by email (most unique) or name
    // Priority: email > name (email is more unique for filtering)
    if (notification.lead_email) {
      navigate(`/leads?search=${encodeURIComponent(notification.lead_email)}`);
    } else if (notification.lead_name) {
      navigate(`/leads?search=${encodeURIComponent(notification.lead_name)}`);
    } else {
      navigate('/leads');
    }
    onDismiss();
  };

  // Navigate to quote page (for quote notifications)
  const handleViewQuote = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Switch to correct project if needed
    const projectId = notification.metadata?.project_id;
    if (projectId && projects.length > 0) {
      const targetProject = projects.find(p => p.id === projectId);
      if (targetProject) {
        setSelectedProject(targetProject);
        await new Promise(resolve => setTimeout(resolve, 150));
      }
    }

    // Navigate to quotes page with search param
    const quoteNumber = notification.metadata?.quote_number;
    if (quoteNumber) {
      navigate(`/quotes?search=${encodeURIComponent(String(quoteNumber))}`);
    } else {
      navigate('/quotes');
    }
    onDismiss();
  };

  // Navigate to leads page filtered by expiring quotes (for grouped notifications)
  const handleViewExpiringLeads = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const projectId = notification.metadata?.project_id;
    if (projectId && projects.length > 0) {
      const targetProject = projects.find(p => p.id === projectId);
      if (targetProject) {
        setSelectedProject(targetProject);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    navigate('/leads?filter=quotesExpiringSoon');
    onDismiss();
  };

  // Determine notification category
  const category = getNotificationCategory(notification.type);
  const isQuote = category === 'quote';
  const isMessage = category === 'message';
  const isInternal = category === 'internal';
  const isGroupedExpiring = notification.type === 'quote_expiring_soon' && !!notification.metadata?.grouped;

  // Navigate to conversation (for message notifications)
  const handleViewConversation = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Switch to correct project if needed
    const projectId = notification.metadata?.project_id;
    if (projectId && projects.length > 0) {
      const targetProject = projects.find(p => p.id === projectId);
      if (targetProject) {
        setSelectedProject(targetProject);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    // Navigate to the correct chat page based on notification type
    const conversationId = notification.metadata?.conversation_id;
    const isInternalNotif = notification.type.startsWith('message_internal_');

    if (isInternalNotif) {
      // Internal (team/direct) messages → team-chat page
      const url = conversationId
        ? `/team-chat?conversation=${encodeURIComponent(String(conversationId))}`
        : '/team-chat';
      navigate(url);
    } else if (conversationId) {
      // External messages → omnichannel page
      const channelType = notification.metadata?.channel_type;
      let url = `/omnichannel?conversation=${encodeURIComponent(String(conversationId))}`;
      if (channelType) {
        url += `&channelType=${encodeURIComponent(String(channelType))}`;
      }
      navigate(url);
    } else {
      navigate('/omnichannel');
    }
    onDismiss();
  };

  return (
    <div
      className="
        group relative p-4
        border-b border-gray-200/50 dark:border-gray-700/50
        last:border-b-0
        transition-all duration-200 ease-out
        hover:bg-gray-50/50 dark:hover:bg-gray-800/30
      "
    >
      {/* Close Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="
          absolute top-3 right-3 z-10
          h-6 w-6 rounded-md
          border border-gray-300 dark:border-gray-600
          bg-white dark:bg-gray-800
          hover:bg-gray-100 dark:hover:bg-gray-700
          flex items-center justify-center
          transition-colors duration-200
        "
        title="Cerrar"
        aria-label="Cerrar notificación"
      >
        <XMarkIcon className="h-3.5 w-3.5 text-gray-300 dark:text-gray-500" />
      </button>

      {/* Content */}
      <div className="space-y-1.5">
        {/* Row 1: Name + Action buttons + Badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {isGroupedExpiring
              ? notification.message
              : isInternal
                ? notification.title
                : (notification.lead_name || notification.title || 'Notificacion')}
          </h3>

          {/* Action buttons — only for lead notifications (not internal/message/quote) */}
          {!isGroupedExpiring && showsContactActions(category) && (
          <div className="flex items-center gap-1">
            {/* Phone - breathing green glow when high priority */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPhoneClick(notification);
              }}
              className="h-5 w-5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-300 dark:text-gray-500"
              title="Llamar"
            >
              <PhoneIcon className="h-2.5 w-2.5" style={breatheStyle} />
            </button>

            {/* WhatsApp */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (notification.lead_phone) {
                  window.open(`https://wa.me/${notification.lead_phone.replace(/\D/g, '')}`, '_blank');
                  onDismiss();
                } else {
                  toast.error('Este lead no tiene número de teléfono');
                }
              }}
              className="h-5 w-5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors duration-200 text-gray-300 dark:text-gray-500"
              title="WhatsApp"
            >
              <ChatBubbleLeftIcon className="h-2.5 w-2.5" style={breatheStyle} />
            </button>

            {/* Email */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (notification.lead_email) {
                  window.open(`mailto:${notification.lead_email}`, '_blank');
                  onDismiss();
                } else {
                  toast.error('Este lead no tiene email');
                }
              }}
              className="h-5 w-5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors duration-200 text-gray-300 dark:text-gray-500"
              title="Email"
            >
              <EnvelopeIcon className="h-2.5 w-2.5" style={breatheStyle} />
            </button>

            {/* Calendar */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCalendarClick(notification);
              }}
              className="h-5 w-5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors duration-200 text-gray-300 dark:text-gray-500"
              title="Agendar"
            >
              <CalendarIcon className="h-2.5 w-2.5" style={breatheStyle} />
            </button>
          </div>
          )}

          {/* Badges for quote notifications - matches QuoteViewingNotification logic */}
          {isQuote && (
            <>
              {/* Aceptada badge - only for accepted quotes */}
              {notification.type === 'quote_accepted' && (
                <Badge variant="lime" size="sm" className="font-semibold">
                  Aceptada
                </Badge>
              )}
              {/* Contacto badge - for contact requested (not accepted) */}
              {notification.type === 'quote_contact_requested' && (
                <Badge variant="amber" size="sm" className="font-semibold">
                  Contacto
                </Badge>
              )}
              {/* En espera badge - for deferred quotes */}
              {notification.type === 'quote_deferred' && (
                <Badge variant="default" size="sm" className="font-semibold !bg-violet-100 !text-violet-700 dark:!bg-violet-900/30 dark:!text-violet-400">
                  En espera
                </Badge>
              )}
              {/* Recontactar badge - for deferred quote follow-up */}
              {notification.type === 'quote_deferred_followup' && (
                <Badge variant="default" size="sm" className="font-semibold !bg-violet-100 !text-violet-700 dark:!bg-violet-900/30 dark:!text-violet-400">
                  Recontactar
                </Badge>
              )}
              {/* Expirada badge */}
              {notification.type === 'quote_expired' && (
                <Badge variant="amber" size="sm" className="font-medium">
                  Expirada
                </Badge>
              )}
              {/* Cotización por expirar badge (only individual, not grouped) */}
              {notification.type === 'quote_expiring_soon' && !isGroupedExpiring && (
                <Badge variant="amber" size="sm" className="font-semibold">
                  Cotización por expirar
                </Badge>
              )}
              {/* Vista count badge - shown for ALL quote types EXCEPT accepted */}
              {notification.type !== 'quote_accepted' && notification.type !== 'quote_expired' && notification.type !== 'quote_expiring_soon' && notification.metadata?.total_views && (
                <Badge variant="lime" size="sm" className="font-medium">
                  Vista #{notification.metadata.total_views}
                </Badge>
              )}
            </>
          )}

          {/* Message Badge - for external message notifications */}
          {isMessage && (
            <Badge variant={getBadgeVariant(notification.type)} size="sm" className="font-medium">
              {getBadgeLabel(notification)}
            </Badge>
          )}

          {/* Internal Message Badge */}
          {isInternal && (
            <Badge
              variant={notification.type === 'message_internal_dm' ? 'purple' as any : 'default'}
              size="sm"
              className={`font-medium ${notification.type === 'message_internal_group' ? '!bg-indigo-100 !text-indigo-700 dark:!bg-indigo-900/30 dark:!text-indigo-400' : ''}`}
            >
              {getBadgeLabel(notification)}
            </Badge>
          )}

          {/* Source Badge - for lead notifications only */}
          {!isQuote && !isMessage && !isInternal && (
            <Badge variant={getBadgeVariant(notification.type)} size="sm" className="font-medium">
              {getBadgeLabel(notification)}
            </Badge>
          )}
        </div>

        {/* Row 2: Phone (only for lead notifications) */}
        {!isGroupedExpiring && !isInternal && !isMessage && notification.lead_phone && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Tel:</span> {notification.lead_phone}
          </p>
        )}

        {/* Row 3: Email (only for lead notifications) */}
        {!isGroupedExpiring && !isInternal && !isMessage && notification.lead_email && (
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
            <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span> {notification.lead_email}
          </p>
        )}

        {/* Row 4: Message preview (for external AND internal message notifications) */}
        {showsMessagePreview(category) && notification.metadata?.message_preview && (
          <p className="text-xs text-gray-600 dark:text-gray-400 italic line-clamp-2">
            &ldquo;{notification.metadata.message_preview}&rdquo;
          </p>
        )}

        {/* Row 5: Project */}
        {notification.metadata?.project_name && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Proyecto:</span> {notification.metadata.project_name}
          </p>
        )}

        {/* Row 5: Stage (only for individual quote notifications) */}
        {isQuote && !isGroupedExpiring && notification.metadata?.stage_name && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Etapa:</span> {notification.metadata.stage_name}
          </p>
        )}

        {/* Row 6: Unit with typology (for individual quote notifications) */}
        {isQuote && !isGroupedExpiring && (notification.metadata?.unit_name || notification.metadata?.unit_code) && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Unidad:</span>{' '}
            {notification.metadata.unit_name || notification.metadata.unit_code}
            {notification.metadata?.typology_name && ` • ${notification.metadata.typology_name}`}
          </p>
        )}

        {/* Row 7: Quote number (for individual quote notifications) */}
        {isQuote && !isGroupedExpiring && notification.metadata?.quote_number && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Código:</span> {notification.metadata.quote_number}
          </p>
        )}

        {/* Row 8: Expiring soon context message (only for individual, not grouped) */}
        {notification.type === 'quote_expiring_soon' && !isGroupedExpiring && (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
            ⏳ {notification.message}
          </p>
        )}

        {/* Row: Source Details (enriched info) - for lead, message, and internal notifications */}
        {!isQuote && <SourceDetails metadata={notification.metadata} type={notification.type} />}

        {/* Row 9: Timestamp + View button */}
        <div className="flex items-center justify-between gap-2 pt-1.5">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {timeAgo}
          </p>

          {/* View expiring leads button for grouped quote_expiring_soon notifications */}
          {isQuote && notification.type === 'quote_expiring_soon' && notification.metadata?.grouped && (
            <button
              onClick={handleViewExpiringLeads}
              className="
                flex items-center gap-1
                text-xs font-medium
                text-gray-900 dark:text-white
                hover:underline
                transition-all duration-200
              "
            >
              <span>Ver leads</span>
              <ArrowRightIcon className="h-3 w-3" />
            </button>
          )}

          {/* View Quote button for individual quote notifications */}
          {isQuote && notification.metadata?.quote_number && !notification.metadata?.grouped && (
            <button
              onClick={handleViewQuote}
              className="
                flex items-center gap-1
                text-xs font-medium
                text-gray-900 dark:text-white
                hover:underline
                transition-all duration-200
              "
            >
              <span>Ver cotización</span>
              <ArrowRightIcon className="h-3 w-3" />
            </button>
          )}

          {/* View Conversation button for message AND internal notifications */}
          {(isMessage || isInternal) && (
            <button
              onClick={handleViewConversation}
              className="
                flex items-center gap-1
                text-xs font-medium
                text-gray-900 dark:text-white
                hover:underline
                transition-all duration-200
              "
            >
              <span>Ver conversacion</span>
              <ArrowRightIcon className="h-3 w-3" />
            </button>
          )}

          {/* View Lead button for lead notifications */}
          {!isQuote && !isMessage && !isInternal && notification.lead_id && (
            <button
              onClick={handleViewLead}
              className="
                flex items-center gap-1
                text-xs font-medium
                text-gray-900 dark:text-white
                hover:underline
                transition-all duration-200
              "
            >
              <span>Ver lead</span>
              <ArrowRightIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationToast: React.FC = () => {
  const location = useLocation();

  // Don't show notifications in OrbitView pages
  const isOrbitViewPage = location.pathname.startsWith('/orbit-view');
  const isOmnichannelPage = location.pathname.startsWith('/omnichannel');
  const isTeamChatPage = location.pathname.startsWith('/team-chat');

  const { notifications, dismiss } = useNotifications({
    enabled: !isOrbitViewPage,
    enableSound: true,
    isInOmnichannel: isOmnichannelPage,
  });

  const [phoneLead, setPhoneLead] = useState<{ name: string; phone: string } | null>(null);
  const [calendarLead, setCalendarLead] = useState<{ id: string; name: string; email?: string; phone?: string } | null>(null);
  const [phoneActioned, setPhoneActioned] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(loadDismissedNotifications);

  // Dismissed IDs are persisted in localStorage with a 24-hour TTL.
  // No cleanup needed here - the TTL in loadDismissedNotifications handles expiration.

  // Auto-dismiss message notifications when entering their respective pages
  useEffect(() => {
    if (isOmnichannelPage) {
      notifications
        .filter(n => n.type.startsWith('message_received_') && n.id && !dismissedIds.has(n.id))
        .forEach(n => dismiss(n.id!));
    }
    if (isTeamChatPage) {
      notifications
        .filter(n => n.type.startsWith('message_internal_') && n.id && !dismissedIds.has(n.id))
        .forEach(n => dismiss(n.id!));
    }
  }, [isOmnichannelPage, isTeamChatPage]); // Only on page change, not on every notification

  const handleDismiss = useCallback((notificationId: string | null) => {
    if (notificationId) {
      setDismissedIds(prev => {
        const next = new Set([...prev, notificationId]);
        saveDismissedNotifications(next);
        return next;
      });
      dismiss(notificationId);
    }
  }, [dismiss]);

  const handlePhoneClick = (notification: Notification) => {
    if (notification.lead_phone) {
      setPhoneLead({
        name: notification.lead_name || 'Lead',
        phone: notification.lead_phone,
      });
      setPhoneActioned(true); // Stop breathing animation
      if (notification.id) handleDismiss(notification.id);
    } else {
      toast.error('Este lead no tiene número de teléfono');
    }
  };

  const handleCalendarClick = (notification: Notification) => {
    if (notification.lead_id) {
      setCalendarLead({
        id: notification.lead_id,
        name: notification.lead_name || 'Lead',
        email: notification.lead_email || undefined,
        phone: notification.lead_phone || undefined,
      });
      if (notification.id) handleDismiss(notification.id);
    }
  };

  // Filter out dismissed notifications
  // All notifications now have an ID (generated client-side if server didn't provide one)
  // Suppress message toasts on their respective pages (card badges are more contextual)
  const visibleNotifications = notifications.filter(
    n => n.id && !dismissedIds.has(n.id)
      && !(isOmnichannelPage && n.type.startsWith('message_received_'))
      && !(isTeamChatPage && n.type.startsWith('message_internal_'))
  );

  if (visibleNotifications.length === 0) {
    return (
      <>
        <PhoneModal
          open={!!phoneLead}
          onClose={() => setPhoneLead(null)}
          phone={phoneLead?.phone || ''}
          name={phoneLead?.name}
          context="Lead"
        />
        <CalendarModal
          open={!!calendarLead}
          onClose={() => setCalendarLead(null)}
          lead={calendarLead || undefined}
        />
      </>
    );
  }

  // Get dominant source for header styling
  const dominantNotification = visibleNotifications[0];
  const dominantCategory = getNotificationCategory(dominantNotification.type);
  // For quotes, messages, and internal — use the type as source config key
  // For leads — use metadata.source (facebook, whatsapp, etc.)
  const dominantSource = (dominantCategory === 'quote' || dominantCategory === 'message' || dominantCategory === 'internal')
    ? dominantNotification.type
    : (dominantNotification?.metadata?.source || 'default');
  const dominantConfig = getSourceConfig(dominantSource);

  // Check if there are high priority notifications
  const hasHighPriority = visibleNotifications.some(isHighPriority);

  // Count notification types by category
  const quoteNotifications = visibleNotifications.filter(n => getNotificationCategory(n.type) === 'quote');
  const messageNotifications = visibleNotifications.filter(n => {
    const cat = getNotificationCategory(n.type);
    return cat === 'message' || cat === 'internal';
  });
  const leadNotifications = visibleNotifications.filter(n => {
    const cat = getNotificationCategory(n.type);
    return cat === 'lead' || cat === 'system';
  });

  // Build title
  let title: string;
  if (visibleNotifications.length === 1) {
    title = visibleNotifications[0].title;
  } else {
    const parts: string[] = [];
    if (leadNotifications.length > 0) {
      parts.push(`${leadNotifications.length} lead${leadNotifications.length > 1 ? 's' : ''}`);
    }
    if (messageNotifications.length > 0) {
      parts.push(`${messageNotifications.length} mensaje${messageNotifications.length > 1 ? 's' : ''}`);
    }
    if (quoteNotifications.length > 0) {
      if (quoteNotifications.length === visibleNotifications.length) {
        // Only quote notifications - show detailed breakdown
        const accepted = quoteNotifications.filter(n => n.type === 'quote_accepted').length;
        const contact = quoteNotifications.filter(n => n.type === 'quote_contact_requested').length;
        const deferred = quoteNotifications.filter(n => n.type === 'quote_deferred' || n.type === 'quote_deferred_followup').length;
        const viewing = quoteNotifications.filter(n => n.type === 'quote_viewing').length;
        const expiring = quoteNotifications.filter(n => n.type === 'quote_expiring_soon').length;
        const expired = quoteNotifications.filter(n => n.type === 'quote_expired').length;

        const quoteParts: string[] = [];
        if (accepted > 0) quoteParts.push(`${accepted} aceptada${accepted > 1 ? 's' : ''}`);
        if (contact > 0) quoteParts.push(`${contact} solicitud${contact > 1 ? 'es' : ''}`);
        if (deferred > 0) quoteParts.push(`${deferred} en espera`);
        if (viewing > 0) quoteParts.push(`${viewing} viendo`);
        if (expiring > 0) quoteParts.push(`${expiring} cotización${expiring > 1 ? 'es' : ''} por expirar`);
        if (expired > 0) quoteParts.push(`${expired} expirada${expired > 1 ? 's' : ''}`);
        parts.push(quoteParts.length > 0 ? quoteParts.join(' + ') : `${quoteNotifications.length} cotizaciones`);
      } else {
        parts.push(`${quoteNotifications.length} cotización${quoteNotifications.length > 1 ? 'es' : ''}`);
      }
    }
    title = parts.join(' + ') || 'Notificaciones';
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full animate-slideUp">
      {/* Glassmorphism Container */}
      <div className="
        relative
        rounded-2xl
        overflow-hidden
        shadow-[0_8px_32px_0_rgba(0,0,0,0.12),0_2px_8px_0_rgba(0,0,0,0.08)]
        dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.4),0_2px_8px_0_rgba(0,0,0,0.2)]
      ">
        {/* Base glass layer */}
        <div className="
          absolute inset-0
          bg-white/[0.75] dark:bg-gray-900/[0.80]
          backdrop-blur-md backdrop-saturate-150
          pointer-events-none
        "></div>

        {/* Light gradient for depth */}
        <div className="
          absolute inset-0
          bg-gradient-to-br from-white/10 via-transparent to-transparent
          dark:from-white/5
          pointer-events-none
        "></div>

        {/* Border */}
        <div className="
          absolute inset-0
          rounded-2xl
          border border-gray-200/60 dark:border-gray-700/60
          pointer-events-none
        "></div>

        {/* Content Container */}
        <div className="relative">
          {/* Header */}
          <div className="
            relative
            px-4 py-3
            border-b border-white/25 dark:border-white/15
          ">
            {/* Header background */}
            <div className="
              absolute inset-0
              bg-white/12 dark:bg-white/8
              backdrop-blur-md
              pointer-events-none
            "></div>

            {/* Header content */}
            <div className="relative flex items-center gap-3">
              {/* Source icon with pulse - color changes based on priority */}
              <div className={`
                flex-shrink-0 w-10 h-10 rounded-full
                backdrop-blur-md
                flex items-center justify-center
                relative border
                ${hasHighPriority
                  ? 'bg-amber-500/25 dark:bg-amber-500/35 border-amber-400/60 dark:border-amber-400/70 shadow-[0_0_25px_rgba(245,158,11,0.4)]'
                  : dominantConfig.bgClass + ' shadow-[0_0_25px_rgba(0,0,0,0.1)]'
                }
              `}>
                {/* Source Icon */}
              <div className="relative flex items-center justify-center">
                {hasHighPriority && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                )}
                {dominantConfig.icon}
              </div>

                {/* Inner shine */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent"></div>
              </div>

              {/* Title */}
              <div className="flex-1 min-w-0">
                <p className="
                  text-sm font-bold text-gray-900 dark:text-white
                  [text-shadow:0_1px_0_rgba(255,255,255,0.3),0_-1px_0_rgba(0,0,0,0.2)]
                  dark:[text-shadow:0_1px_0_rgba(255,255,255,0.2),0_-1px_0_rgba(0,0,0,0.4)]
                ">
                  {title}
                </p>
                <p className="
                  text-xs font-medium text-gray-800 dark:text-gray-200
                  [text-shadow:0_1px_0_rgba(255,255,255,0.2),0_-1px_0_rgba(0,0,0,0.15)]
                ">
                  En tiempo real
                </p>
              </div>
            </div>
          </div>

          {/* Notification Items List */}
          <div className="max-h-96 overflow-y-auto">
            {visibleNotifications.slice(0, 5).map(notification => (
              <NotificationItem
                key={notification.id || Math.random()}
                notification={notification}
                onDismiss={() => handleDismiss(notification.id)}
                onPhoneClick={handlePhoneClick}
                onCalendarClick={handleCalendarClick}
                phoneActioned={phoneActioned}
              />
            ))}
          </div>

          {/* Footer with count */}
          {visibleNotifications.length > 5 && (
            <div className="
              relative
              px-4 py-2
              border-t border-white/25 dark:border-white/15
            ">
              <div className="
                absolute inset-0
                bg-white/12 dark:bg-white/8
                backdrop-blur-md
                pointer-events-none
              "></div>

              <p className="
                relative text-xs text-gray-800 dark:text-gray-200 text-center font-semibold
                [text-shadow:0_1px_0_rgba(255,255,255,0.2),0_-1px_0_rgba(0,0,0,0.15)]
              ">
                +{visibleNotifications.length - 5} más
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Phone Modal */}
      <PhoneModal
        open={!!phoneLead}
        onClose={() => setPhoneLead(null)}
        phone={phoneLead?.phone || ''}
        name={phoneLead?.name}
        context="Lead"
      />

      {/* Calendar Modal */}
      <CalendarModal
        open={!!calendarLead}
        onClose={() => setCalendarLead(null)}
        lead={calendarLead || undefined}
      />
    </div>
  );
};

export default NotificationToast;
